import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { printOpenAccountAbonoTicket } from './abono-ticket-print';
import { APPLY_ABONO_MUTATION } from './open-accounts.gql';

import { PAYMENT_METHODS_QUERY } from '@/modules/cashier-pos/cashier-pos.gql';
import { centsToMajorString, formatCentsString, majorStringToCents } from '@/shared/lib/money';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { useAuthStore } from '@/stores/authStore';

interface PaymentMethod {
  id: string;
  code: string;
  name: string;
  requires_reference: boolean;
  affects_cash_count: boolean;
}

interface PaymentMethodsResponse {
  paymentMethods: { items: PaymentMethod[] };
}

interface AbonoDraft {
  payment_method_id: string;
  amount_cents: string;
  reference_number: string | null;
  method_code: string;
}

interface OpenAccount {
  id: string;
  folio_number?: string | null;
  kind?: string;
  balance_cents: string;
  tutor_name?: string | null;
  pet_name?: string | null;
  customer_id?: string | null;
  pet_id?: string | null;
}

interface CoveredConcept {
  sale_folio_number: string | null;
  item_code: string;
  item_name: string;
  quantity: string;
  total_cents: string;
  covered_cents: string;
  pending_cents: string;
  coverage_status: string;
}

interface ApplyAbonoResponse {
  applyAbono: {
    balance_before_cents: string;
    balance_after_cents: string;
    ticket_id: string | null;
    ticket_folio_number: string | null;
    covered_concepts: CoveredConcept[];
    account: OpenAccount;
  };
}

interface ApplyAbonoDialogProps {
  account: OpenAccount;
  on_close: () => void;
  on_submitted: () => void | Promise<void>;
  on_error: (msg: string | null) => void;
}

export function ApplyAbonoDialog({
  account,
  on_close,
  on_submitted,
  on_error,
}: ApplyAbonoDialogProps): JSX.Element {
  const { t } = useTranslation();
  const cashier_name = useAuthStore((s) => s.user?.full_name ?? 'Caja');
  const { data: methods_data } = useQuery<PaymentMethodsResponse>(PAYMENT_METHODS_QUERY, {
    variables: { offset: 0, limit: 50 },
    fetchPolicy: 'cache-first',
  });
  const methods = useMemo(
    () =>
      (methods_data?.paymentMethods.items ?? []).filter((m) => m.code !== 'open_account'),
    [methods_data],
  );

  const balance = BigInt(account.balance_cents);
  const [drafts, setDrafts] = useState<AbonoDraft[]>([]);
  const [note, setNote] = useState('');
  const can_overpay = account.kind === 'hospitalization';

  useEffect(() => {
    if (drafts.length === 0 && methods.length > 0) {
      const cash = methods.find((m) => m.code === 'cash') ?? methods[0];
      if (cash) {
        setDrafts([
          {
            payment_method_id: cash.id,
            amount_cents: balance > 0n ? account.balance_cents : '0',
            reference_number: cash.requires_reference ? '' : null,
            method_code: cash.code,
          },
        ]);
      }
    }
  }, [methods, drafts.length, account.balance_cents]);

  const total_cents = drafts.reduce<bigint>((acc, d) => acc + safeBigInt(d.amount_cents, 0n), 0n);
  const exceeds_balance = total_cents > balance;
  const missing_reference = drafts.some(
    (d) =>
      methods.find((m) => m.id === d.payment_method_id)?.requires_reference &&
      !(d.reference_number?.trim()),
  );
  const all_positive = drafts.every((d) => safeBigInt(d.amount_cents, 0n) > 0n);

  const [applyAbono, { loading }] = useMutation<ApplyAbonoResponse>(APPLY_ABONO_MUTATION);
  const can_submit =
    drafts.length > 0 &&
    all_positive &&
    (can_overpay || !exceeds_balance) &&
    !missing_reference &&
    total_cents > 0n &&
    !loading;

  const onClickAdd = (): void => {
    const fallback = methods[0];
    if (!fallback) return;
    setDrafts((prev) => [
      ...prev,
      {
        payment_method_id: fallback.id,
        amount_cents: '0',
        reference_number: fallback.requires_reference ? '' : null,
        method_code: fallback.code,
      },
    ]);
  };

  const onChangeMethod = (index: number, method_id: string): void => {
    const m = methods.find((mm) => mm.id === method_id);
    if (!m) return;
    setDrafts((prev) =>
      prev.map((d, i) =>
        i === index
          ? {
              ...d,
              payment_method_id: m.id,
              method_code: m.code,
              reference_number: m.requires_reference ? d.reference_number ?? '' : null,
            }
          : d,
      ),
    );
  };

  const onChangeAmountMajor = (index: number, value: string): void => {
    setDrafts((prev) =>
      prev.map((d, i) => {
        if (i !== index) return d;
        try {
          return { ...d, amount_cents: majorStringToCents(value || '0').toString() };
        } catch {
          return d;
        }
      }),
    );
  };

  const onChangeReference = (index: number, value: string): void => {
    setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, reference_number: value } : d)));
  };

  const onClickRemove = (index: number): void => {
    setDrafts((prev) => prev.filter((_, i) => i !== index));
  };

  const onClickSubmit = async (): Promise<void> => {
    on_error(null);
    const idempotency_key =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `np-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      const result = await applyAbono({
        variables: {
          input: {
            open_account_id: account.id,
            payments: drafts.map((d) => {
              const trimmed = d.reference_number?.trim();
              return {
                payment_method_id: d.payment_method_id,
                amount_cents: d.amount_cents,
                reference_number: trimmed && trimmed.length > 0 ? trimmed : null,
              };
            }),
            note: note.trim() || null,
          },
        },
        // eslint-disable-next-line @typescript-eslint/naming-convention
        context: { headers: { 'Idempotency-Key': idempotency_key } },
      });
      const payload = result.data?.applyAbono;
      if (payload) {
        const target_account = payload.account ?? account;
        await printOpenAccountAbonoTicket({
          account_folio_number: target_account.folio_number ?? null,
          account_id_fallback: target_account.id,
          tutor_name: target_account.tutor_name ?? null,
          pet_name: target_account.pet_name ?? null,
          total_cents: total_cents.toString(),
          balance_before_cents: payload.balance_before_cents,
          balance_after_cents: payload.balance_after_cents,
          ticket_folio_number: payload.ticket_folio_number,
          covered_concepts: payload.covered_concepts.map((concept) => ({
            item_code: concept.item_code,
            item_name: concept.item_name,
            quantity: concept.quantity,
            covered_cents: concept.covered_cents,
          })),
          payments: drafts.map((draft) => {
            const method = methods.find((candidate) => candidate.id === draft.payment_method_id);
            return {
              payment_method_name: method?.name ?? draft.method_code,
              amount_cents: draft.amount_cents,
              reference_number: draft.reference_number?.trim() || null,
            };
          }),
          cashier_name,
          note: note.trim() || null,
          paid_at_iso: new Date().toISOString(),
          mode: 'ticket',
        });
      }
      await on_submitted();
    } catch (err) {
      if (err instanceof ApolloError) {
        const code = err.graphQLErrors[0]?.message ?? err.message;
        if (code === 'abono_exceeds_balance') {
          on_error(t('open_accounts.error_exceeds_balance'));
          return;
        }
        if (code === 'cash_shift_required') {
          on_error(t('open_accounts.error_shift_required'));
          return;
        }
        if (code.startsWith('payment_reference_required')) {
          on_error(t('open_accounts.error_reference_required'));
          return;
        }
        on_error(code);
        return;
      }
      if (err instanceof Error) on_error(err.message);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>{t('open_accounts.apply_abono_title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-md border border-border bg-card/40 p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('open_accounts.balance')}</span>
              <span className="font-mono tabular-nums">
                {formatCentsString(account.balance_cents)}
              </span>
            </div>
            {can_overpay ? (
              <p className="mt-1 text-xs text-muted-foreground">
                {t('open_accounts.hospitalization_credit_hint')}
              </p>
            ) : null}
            <div className="mt-1 flex items-center justify-between">
              <span className="text-muted-foreground">{t('open_accounts.abono_total')}</span>
              <span className="font-mono tabular-nums">
                {formatCentsString(total_cents.toString())}
              </span>
            </div>
            {exceeds_balance && !can_overpay ? (
              <p className="mt-1 text-xs text-destructive">
                {t('open_accounts.error_exceeds_balance')}
              </p>
            ) : null}
          </div>

          <ul className="space-y-2">
            {drafts.map((draft, index) => {
              const method = methods.find((m) => m.id === draft.payment_method_id);
              const requires_ref = method?.requires_reference ?? false;
              return (
                <li
                  key={index}
                  className="space-y-2 rounded-md border border-border bg-card/30 p-3"
                >
                  <div className="flex items-center gap-2">
                    <select
                      className="flex h-9 flex-1 rounded-md border border-input bg-background px-2 text-sm"
                      value={draft.payment_method_id}
                      onChange={(e) => onChangeMethod(index, e.target.value)}
                      disabled={loading}
                    >
                      {methods.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                    {drafts.length > 1 ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => onClickRemove(index)}
                        disabled={loading}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" aria-hidden />
                      </Button>
                    ) : null}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">{t('open_accounts.amount')}</Label>
                      <Input
                        inputMode="decimal"
                        className="font-mono"
                        value={centsToMajorString(draft.amount_cents)}
                        onChange={(e) => onChangeAmountMajor(index, e.target.value)}
                        disabled={loading}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">
                        {t('open_accounts.reference')}
                        {requires_ref ? ' *' : ''}
                      </Label>
                      <Input
                        value={draft.reference_number ?? ''}
                        onChange={(e) => onChangeReference(index, e.target.value)}
                        disabled={loading || !requires_ref}
                        placeholder={requires_ref ? '...' : '-'}
                      />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          <Button type="button" size="sm" variant="outline" onClick={onClickAdd} disabled={loading}>
            <Plus className="h-4 w-4" aria-hidden />
            <span>{t('open_accounts.add_payment')}</span>
          </Button>

          <div className="space-y-1">
            <Label htmlFor="abono-note">{t('open_accounts.note')}</Label>
            <Input
              id="abono-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={loading}
            />
          </div>
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={!can_submit}
          >
            {loading ? t('common.loading') : t('open_accounts.apply_abono')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

function safeBigInt(s: string, fallback: bigint): bigint {
  try {
    if (!/^-?\d+$/.test(s)) return fallback;
    return BigInt(s);
  } catch {
    return fallback;
  }
}
